import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { first } from 'rxjs/operators';
import { ReportService } from '@app/_components/report';
import { RentalsUnitedService } from '../..';

/**
 * Hotel Room V2 — Quality Check Dashboard.
 *
 * Compares WIKI (internal DB) vs live Rentals United availability and LOS pricing
 * for any property (standard STR, composite, or hotel-room type 3).
 *
 * Calls POST /api/2.6/rentals-united/quality-check.
 * Displays:
 *   - Summary stat cards (days checked, avail mismatches, nights checked, price mismatches)
 *   - Day-by-day availability table (all dates, colour-coded match/mismatch)
 *   - Day-by-day LOS pricing matrix (dates × nights 1-7, WIKI / RU / diff%)
 */
@Component({
    selector: 'app-ru-quality-check',
    templateUrl: './ru-quality-check.component.html',
    styleUrls: ['./ru-quality-check.component.css'],
    standalone: false
})
export class RuQualityCheckComponent implements OnInit, OnChanges {

    @Input() ruAccount: string = 'prod';

    // -------------------------------------------------------------------------
    // Form
    // -------------------------------------------------------------------------
    form1: UntypedFormGroup;
    formLoading = false;
    shortTermRentals: any[] = [];

    validationMessages = {
        shortTermRentalId: [{ type: 'required', message: 'Property is required' }],
    };

    // -------------------------------------------------------------------------
    // Results
    // -------------------------------------------------------------------------
    result: any = null;
    summary: any = null;
    dailyAvailability: any[] = [];
    dailyPricing: any[] = [];
    availabilityMismatches: any[] = [];
    priceMismatches: any[] = [];

    /** Fast lookup: date string → Map<numNights, nightData> */
    private pricingMap: Map<string, Map<number, any>> = new Map();

    readonly nightRange = [1, 2, 3, 4, 5, 6, 7];

    // -------------------------------------------------------------------------
    // Filter toggles
    // -------------------------------------------------------------------------
    showOnlyAvailMismatches = false;
    showOnlyPriceMismatches = false;

    // -------------------------------------------------------------------------
    constructor(
        private rentalsUnitedService: RentalsUnitedService,
        private formBuilder: UntypedFormBuilder,
        private messageService: MessageService,
        private reportService: ReportService,
    ) {}

    ngOnInit(): void {
        this.buildForm();
        this.loadShortTermRentals();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ruAccount'] && this.form1) {
            this.form1.patchValue({ ruAccount: this.ruAccount });
        }
    }

    buildForm(): void {
        const today = new Date();
        const plus90 = new Date();
        plus90.setDate(today.getDate() + 90);

        this.form1 = this.formBuilder.group({
            shortTermRentalId: ['', Validators.required],
            startDate: [today],
            endDate: [plus90],
            ruAccount: [this.ruAccount],
        });
    }

    loadShortTermRentals(): void {
        this.reportService.propertySummaryList().subscribe(
            res => { this.shortTermRentals = res.data.propertySummaryList ?? []; },
            err => {
                this.messageService.add({ severity: 'error', summary: 'Load Failed',
                    detail: err?.message ?? 'Could not load property list.', life: 5000 });
            }
        );
    }

    // -------------------------------------------------------------------------
    // Run check
    // -------------------------------------------------------------------------
    onRunCheck(): void {
        if (this.form1.invalid) { this.form1.markAllAsTouched(); return; }
        this.formLoading = true;
        this.clearResults();

        this.rentalsUnitedService.qualityCheck(this.form1.value)
            .pipe(first())
            .subscribe(
                (res: any) => {
                    const qc = res.data?.qualityCheck ?? {};
                    this.summary               = qc.summary ?? {};
                    this.dailyAvailability     = qc.dailyAvailability ?? [];
                    this.dailyPricing          = qc.dailyPricing ?? [];
                    this.availabilityMismatches = qc.availabilityMismatches ?? [];
                    this.priceMismatches       = qc.priceMismatches ?? [];
                    this.result                = qc;
                    this.buildPricingMap();

                    const passed = this.summary.allAvailabilityMatch && this.summary.allPricesMatch;
                    this.messageService.add({
                        severity: passed ? 'success' : 'warn',
                        summary: passed ? 'Quality Check PASSED ✓' : 'Quality Check FAILED ✗',
                        detail: res.message,
                        life: 7000
                    });
                },
                (err: any) => {
                    this.formLoading = false;
                    this.messageService.add({ severity: 'error', summary: 'Quality Check Error',
                        detail: err?.error?.message ?? err?.message ?? 'Unknown error', life: 10000 });
                },
                () => { this.formLoading = false; }
            );
    }

    private clearResults(): void {
        this.result = null;
        this.summary = null;
        this.dailyAvailability = [];
        this.dailyPricing = [];
        this.availabilityMismatches = [];
        this.priceMismatches = [];
        this.pricingMap.clear();
    }

    // -------------------------------------------------------------------------
    // Pricing matrix helpers
    // -------------------------------------------------------------------------
    private buildPricingMap(): void {
        this.pricingMap = new Map();
        for (const row of this.dailyPricing) {
            const nMap = new Map<number, any>();
            if (row.nights) {
                for (const n of row.nights) { nMap.set(n.numNights, n); }
            }
            this.pricingMap.set(row.date, nMap);
        }
    }

    getNightData(date: string, numNights: number): any {
        return this.pricingMap.get(date)?.get(numNights) ?? null;
    }

    priceCellClass(date: string, numNights: number): string {
        const nd = this.getNightData(date, numNights);
        if (!nd) return '';
        return nd.match ? 'cell-ok' : 'cell-bad';
    }

    // -------------------------------------------------------------------------
    // Availability row class
    // -------------------------------------------------------------------------
    availRowClass(row: any): string {
        if (!row.match) return 'row-mismatch';
        return row.wikiAvailable ? 'row-available' : 'row-blocked';
    }

    // -------------------------------------------------------------------------
    // Filtered views
    // -------------------------------------------------------------------------
    get filteredAvail(): any[] {
        if (this.showOnlyAvailMismatches) { return this.dailyAvailability.filter(r => !r.match); }
        return this.dailyAvailability;
    }

    get filteredPricing(): any[] {
        if (this.showOnlyPriceMismatches) {
            return this.dailyPricing.filter(r => r.nights?.some((n: any) => !n.match));
        }
        return this.dailyPricing;
    }

    // -------------------------------------------------------------------------
    // Display helpers
    // -------------------------------------------------------------------------
    overallStatus(): 'success' | 'warning' | 'danger' | 'info' {
        if (!this.summary) return 'info';
        if (this.summary.allAvailabilityMatch && this.summary.allPricesMatch) return 'success';
        return 'danger';
    }

    listingTypeName(lt: number): string {
        switch (lt) {
            case 1: return 'Standard STR';
            case 2: return 'Composite';
            case 3: return 'Hotel Room';
            default: return 'Unknown (' + lt + ')';
        }
    }

    diffClass(diffPct: number | null): string {
        if (diffPct === null || diffPct === undefined) return 'text-orange-500';
        if (diffPct === 0) return 'text-green-600';
        if (diffPct <= 5) return 'text-yellow-600';
        return 'text-red-600';
    }
}
